import type {IssuePickerItem, SuggestionSelection, SuggestionsShown} from '@github-ui/item-picker/IssuePicker'
import {type PickerType, RepositoryAndIssuePicker} from '@github-ui/item-picker/RepositoryAndIssuePicker'
import {getSemanticSearchType} from '@github-ui/item-picker-base/shared'
import {noop} from '@github-ui/noop'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback, useEffect, useEffectEvent, useMemo, useState} from 'react'
import {graphql, useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../../constants/labels'
import type {OptimisticDependencyIssue} from '../../../mutations/add-blocked-by-mutation'
import {addBlockedByMutation} from '../../../mutations/add-blocked-by-mutation'
import {addRelatesToMutation} from '../../../mutations/add-relates-to-mutation'
import {removeBlockedByMutation} from '../../../mutations/remove-blocked-by-mutation'
import {removeRelatesToMutation} from '../../../mutations/remove-relates-to-mutation'
import type {
  DependenciesPickerRelationshipsQuery,
  DependenciesPickerRelationshipsQuery$data,
} from './__generated__/DependenciesPickerRelationshipsQuery.graphql'
import styles from './DependenciesPicker.module.css'
import {RELATES_TO_SEMANTIC_SUGGESTIONS_FEATURE_FLAG} from './semantic-suggestions-feature-flag'

export type DependenciesPickerProps = {
  type: 'blockedBy' | 'blocking' | 'relatesTo'
  onClose?: () => void
  issueId: string
  issueTitle: string
  organization: string
  defaultRepositoryNameWithOwner: string
  anchorRef: React.RefObject<HTMLElement | null>
  onError?: (errors: Error[]) => void
  onSelection: () => void
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const DependenciesPickerRelationshipsGraphqlQuery = graphql`
  query DependenciesPickerRelationshipsQuery($id: ID!) {
    node(id: $id) {
      ... on Issue {
        id
        blockedBy(first: 100) {
          nodes {
            id
          }
        }
        blocking(first: 100) {
          nodes {
            id
          }
        }
        relatesTo(first: 100) {
          nodes {
            id
          }
        }
      }
    }
  }
`

function diffSelection(selectedIssues: IssuePickerItem[], currentIds: string[]) {
  const selectedIds = new Set(selectedIssues.map(issue => issue.id))
  const currentIdSet = new Set(currentIds)
  return {
    issuesToAdd: [...selectedIds].filter(id => !currentIdSet.has(id)),
    issuesToRemove: [...currentIdSet].filter(id => !selectedIds.has(id)),
  }
}

function toOptimistic(item: IssuePickerItem): OptimisticDependencyIssue {
  return {
    id: item.id,
    title: item.title,
    url: item.url,
    number: item.number,
    state: item.state,
    stateReason: item.stateReason,
    repository: {
      id: item.repository.id,
      nameWithOwner: item.repository.nameWithOwner,
    },
  }
}

// Collapse a mutation's two failure channels — a top-level GraphQL error (e.g. FORBIDDEN, arriving
// as the second onCompleted arg) and a payload-level validation error — into one Error, top-level first.
function toMutationError(
  topLevelErrors?: ReadonlyArray<{readonly message: string}> | null,
  payloadErrors?: ReadonlyArray<{readonly message: string} | null> | null,
): Error | null {
  const topLevelMessage = topLevelErrors?.find(error => error?.message)?.message
  if (topLevelMessage) return new Error(topLevelMessage)

  const payloadMessage = payloadErrors?.find(error => error?.message)?.message
  if (payloadMessage) return new Error(payloadMessage)

  return null
}

export function DependenciesPicker({
  type,
  onClose,
  issueId,
  issueTitle,
  organization,
  defaultRepositoryNameWithOwner,
  anchorRef,
  onError = noop,
  onSelection,
}: DependenciesPickerProps) {
  const environment = useRelayEnvironment()
  const [state, setState] = useState<'loading' | 'error' | 'success' | 'idle'>('idle')
  const [queryData, setQueryData] = useState<DependenciesPickerRelationshipsQuery$data | null>(null)
  const {sendAnalyticsEvent} = useAnalytics()

  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     app-payload flag (repo/org/user gated), so it must be read with useFeatureFlag, not isFeatureEnabled */
  const semanticSuggestionsEnabled = useFeatureFlag(RELATES_TO_SEMANTIC_SUGGESTIONS_FEATURE_FLAG)

  // Only the "Relates to" picker suggests similar issues; blocked-by/blocking share this component.
  const semanticQuery = type === 'relatesTo' && semanticSuggestionsEnabled ? issueTitle : undefined

  // The flag being on doesn't mean semantic search ran (it's unavailable on GHES and ineligible
  // repos), so track availability separately to keep the experiment's treatment group clean.
  const semanticAvailable = Boolean(semanticQuery) && getSemanticSearchType(environment) !== undefined

  const fetchExistingRelationships = useEffectEvent(async () => {
    setState('loading')
    clientSideRelayFetchQueryRetained<DependenciesPickerRelationshipsQuery>({
      environment,
      query: DependenciesPickerRelationshipsGraphqlQuery,
      variables: {id: issueId},
    }).subscribe({
      next: data => {
        setQueryData(data)
        setState('success')
      },
      error: () => {
        setState('error')
      },
    })
  })

  useEffect(() => {
    if (state !== 'idle') return
    if (type === 'blockedBy' || type === 'blocking' || type === 'relatesTo') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      fetchExistingRelationships()
    }
  }, [environment, issueId, state, type])

  const [pickerType, setPickerType] = useState<PickerType>('Issue')

  const blockedByNodes = queryData?.node?.blockedBy?.nodes
  const blockingNodes = queryData?.node?.blocking?.nodes
  const relatesToNodes = queryData?.node?.relatesTo?.nodes

  const blockedByIssueIds = useMemo(() => {
    if (!blockedByNodes) return []
    return blockedByNodes.filter(issue => !!issue).map(issue => issue.id)
  }, [blockedByNodes])

  const blockingIssueIds = useMemo(() => {
    if (!blockingNodes) return []
    return blockingNodes.filter(issue => !!issue).map(issue => issue.id)
  }, [blockingNodes])

  const relatesToIssueIds = useMemo(() => {
    if (!relatesToNodes) return []
    return relatesToNodes.filter(issue => !!issue).map(issue => issue.id)
  }, [relatesToNodes])

  const onPickerTypeChange = useCallback(
    (t: PickerType) => {
      setPickerType(t)
      if (t === null && onClose) {
        onClose()
        setState('idle')
      }
    },
    [onClose],
  )

  const commitBlockedBy = useCallback(
    async (selectedIssues: IssuePickerItem[]) => {
      const issueById = new Map(selectedIssues.map(issue => [issue.id, issue]))
      const {issuesToAdd, issuesToRemove} = diffSelection(selectedIssues, blockedByIssueIds)

      sendAnalyticsEvent(`issue_viewer.dependencies_picker.blocked_by`, 'ISSUE_DEPENDENCIES_PICKER', {
        issuesToAdd: issuesToAdd.length,
        issuesToRemove: issuesToRemove.length,
      })

      const mutationErrorPromises: Array<Promise<Error | null>> = [
        ...issuesToAdd.map(
          blockingIssueId =>
            new Promise<Error | null>(resolve => {
              addBlockedByMutation({
                environment,
                input: {issueId, blockingIssueId},
                onCompleted: (_response, errors) => resolve(toMutationError(errors)),
                onError: resolve,
                optimisticBlockingIssue: issueById.has(blockingIssueId)
                  ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    toOptimistic(issueById.get(blockingIssueId)!)
                  : undefined,
                optimisticDirection: 'blockedBy',
              })
            }),
        ),
        ...issuesToRemove.map(
          blockingIssueId =>
            new Promise<Error | null>(resolve => {
              removeBlockedByMutation({
                environment,
                input: {issueId, blockingIssueId},
                onCompleted: (_response, errors) => resolve(toMutationError(errors)),
                onError: resolve,
                optimisticDirection: 'blockedBy',
              })
            }),
        ),
      ]

      const errors = (await Promise.all(mutationErrorPromises)).flatMap(error => error ?? [])
      onError(errors)
    },
    [blockedByIssueIds, sendAnalyticsEvent, environment, issueId, onError],
  )

  const commitBlocking = useCallback(
    async (selectedIssues: IssuePickerItem[]) => {
      const issueById = new Map(selectedIssues.map(issue => [issue.id, issue]))
      const {issuesToAdd, issuesToRemove} = diffSelection(selectedIssues, blockingIssueIds)

      sendAnalyticsEvent(`issue_viewer.dependencies_picker.blocking`, 'ISSUE_DEPENDENCIES_PICKER', {
        issuesToAdd: issuesToAdd.length,
        issuesToRemove: issuesToRemove.length,
      })

      // 'blocking' inverts the relationship: the selected issue is blocked by the current issue.
      const mutationErrorPromises: Array<Promise<Error | null>> = [
        ...issuesToAdd.map(
          blockedIssueId =>
            new Promise<Error | null>(resolve => {
              addBlockedByMutation({
                environment,
                input: {issueId: blockedIssueId, blockingIssueId: issueId},
                onCompleted: (_response, errors) => resolve(toMutationError(errors)),
                onError: resolve,
                optimisticBlockingIssue: issueById.has(blockedIssueId)
                  ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    toOptimistic(issueById.get(blockedIssueId)!)
                  : undefined,
                optimisticDirection: 'blocking',
              })
            }),
        ),
        ...issuesToRemove.map(
          blockedIssueId =>
            new Promise<Error | null>(resolve => {
              removeBlockedByMutation({
                environment,
                input: {issueId: blockedIssueId, blockingIssueId: issueId},
                onCompleted: (_response, errors) => resolve(toMutationError(errors)),
                onError: resolve,
                optimisticDirection: 'blocking',
              })
            }),
        ),
      ]

      const errors = (await Promise.all(mutationErrorPromises)).flatMap(error => error ?? [])
      onError(errors)
    },
    [blockingIssueIds, sendAnalyticsEvent, environment, issueId, onError],
  )

  const commitRelatesTo = useCallback(
    async (selectedIssues: IssuePickerItem[]) => {
      const issueById = new Map(selectedIssues.map(issue => [issue.id, issue]))
      const {issuesToAdd, issuesToRemove} = diffSelection(selectedIssues, relatesToIssueIds)

      sendAnalyticsEvent(`issue_viewer.dependencies_picker.relates_to`, 'ISSUE_DEPENDENCIES_PICKER', {
        issuesToAdd: issuesToAdd.length,
        issuesToRemove: issuesToRemove.length,
        semanticEnabled: semanticSuggestionsEnabled,
        semanticAvailable,
      })

      const mutationErrorPromises: Array<Promise<Error | null>> = [
        ...issuesToAdd.map(
          relatedIssueId =>
            new Promise<Error | null>(resolve => {
              addRelatesToMutation({
                environment,
                input: {issueId, relatedIssueId},
                onCompleted: (response, errors) => {
                  resolve(toMutationError(errors, response?.addRelatesTo?.errors))
                },
                onError: resolve,
                optimisticRelatedIssue: issueById.has(relatedIssueId)
                  ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    toOptimistic(issueById.get(relatedIssueId)!)
                  : undefined,
              })
            }),
        ),
        ...issuesToRemove.map(
          relatedIssueId =>
            new Promise<Error | null>(resolve => {
              removeRelatesToMutation({
                environment,
                input: {issueId, relatedIssueId},
                onCompleted: (response, errors) => {
                  resolve(toMutationError(errors, response?.removeRelatesTo?.errors))
                },
                onError: resolve,
              })
            }),
        ),
      ]

      const errors = (await Promise.all(mutationErrorPromises)).flatMap(error => error ?? [])
      onError(errors)
    },
    [
      relatesToIssueIds,
      sendAnalyticsEvent,
      semanticSuggestionsEnabled,
      semanticAvailable,
      environment,
      issueId,
      onError,
    ],
  )

  const onIssueSelection = useCallback(
    async (selectedIssues: IssuePickerItem[]) => {
      if (type === 'blockedBy') {
        await commitBlockedBy(selectedIssues)
      } else if (type === 'blocking') {
        await commitBlocking(selectedIssues)
      } else if (type === 'relatesTo') {
        await commitRelatesTo(selectedIssues)
      }
      onSelection()
    },
    [type, commitBlockedBy, commitBlocking, commitRelatesTo, onSelection],
  )

  // Basic engagement telemetry for the semantic suggestions experiment (github/issues#23315): which
  // suggestion the viewer picked, whether it came from semantic or lexical search, its rank, and
  // whether they picked before typing. `semanticEnabled`/`semanticAvailable` give the experiment arm.
  const onSuggestionSelected = useCallback(
    (selection: SuggestionSelection) => {
      sendAnalyticsEvent(
        'issue_viewer.dependencies_picker.relates_to.suggestion_selected',
        'ISSUE_DEPENDENCIES_PICKER',
        {
          ...selection,
          semanticEnabled: semanticSuggestionsEnabled,
          semanticAvailable,
        },
      )
    },
    [sendAnalyticsEvent, semanticSuggestionsEnabled, semanticAvailable],
  )

  const onSearchTermEntered = useCallback(() => {
    sendAnalyticsEvent('issue_viewer.dependencies_picker.relates_to.search_typed', 'ISSUE_DEPENDENCIES_PICKER', {
      semanticEnabled: semanticSuggestionsEnabled,
      semanticAvailable,
    })
  }, [sendAnalyticsEvent, semanticSuggestionsEnabled, semanticAvailable])

  // The impression event: fires once when the initial suggestion list is shown, giving the
  // denominator for "what fraction of sessions engaged with the suggestions?".
  const onSuggestionsShown = useCallback(
    (metadata: SuggestionsShown) => {
      sendAnalyticsEvent('issue_viewer.dependencies_picker.relates_to.suggestions_shown', 'ISSUE_DEPENDENCIES_PICKER', {
        ...metadata,
        semanticEnabled: semanticSuggestionsEnabled,
        semanticAvailable,
      })
    },
    [sendAnalyticsEvent, semanticSuggestionsEnabled, semanticAvailable],
  )

  const hiddenIssueIds = useMemo(() => {
    const ids = [issueId]
    if (type === 'blockedBy') {
      ids.push(...blockingIssueIds)
    } else if (type === 'blocking') {
      ids.push(...blockedByIssueIds)
    }
    return ids
  }, [type, issueId, blockingIssueIds, blockedByIssueIds])

  const selectedIssueIds =
    type === 'blockedBy' ? blockedByIssueIds : type === 'blocking' ? blockingIssueIds : relatesToIssueIds

  const subtitle =
    type === 'blockedBy'
      ? 'Mark current issue as blocked by…'
      : type === 'blocking'
        ? 'Mark current issue as blocking…'
        : LABELS.relatesToPickerSubtitle

  return (
    <RepositoryAndIssuePicker
      anchorElement={props => {
        const {ref} = props as {
          ref: React.RefObject<HTMLElement | null>
        }
        if (ref) {
          ref.current = anchorRef.current
        }
        return <></>
      }}
      onIssueSelection={onIssueSelection}
      organization={organization}
      defaultRepositoryNameWithOwner={defaultRepositoryNameWithOwner}
      pickerType={pickerType}
      onPickerTypeChange={onPickerTypeChange}
      selectedIssueIds={selectedIssueIds}
      hiddenIssueIds={hiddenIssueIds}
      issuePickerProps={{
        selectionVariant: 'multiple',
        isLoading: state === 'loading',
        subtitle,
        // "relates to" is symmetric and needs triage on the target, so only offer candidates the
        // viewer can relate to (blocking/blocked-by need only read access).
        requireTargetTriage: type === 'relatesTo',
        className: styles.DependenciesPicker,
        semanticQuery,
        // Only the "relates to" picker surfaces suggestions worth measuring engagement on.
        ...(type === 'relatesTo' ? {onSuggestionSelected, onSearchTermEntered, onSuggestionsShown} : {}),
        notice:
          state === 'error'
            ? {
                variant: 'error',
                text: `We couldn't load the issues. Try reloading the page, or if the problem persists, contact support.`,
              }
            : undefined,
      }}
    />
  )
}
